"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Form = void 0;
const rules_engine_1 = require("@mylicenseone/rules-engine");
const events_1 = require("events");
const text_mask_addons_1 = require("text-mask-addons");
const lodash_1 = require("lodash");
const jsonpath_1 = __importDefault(require("jsonpath"));
const moment_1 = __importDefault(require("moment"));
const regexOperators = ['match', 'matches', 'doesNotMatch', 'repeatableMatches', 'repeatableDoesNotMatch'];
function internalFact(name) {
    return `[__internal__]${name}`;
}
const nestedFormInstanceFact = internalFact('nestedFormInstance');
const visibilityStateFact = internalFact('visibilityState');
const enabledStateFact = internalFact('enabledState');
function stringToRegex(str) {
    if (str === undefined || str === null) {
        return str;
    }
    const match = str.match(/^\/(.*)\/(.*)$/);
    if (match) {
        return new RegExp(match[1], match[2]);
    }
    return new RegExp(str);
}
/**
 * Engine for applying business logic specified in a form definition to an
 * application.  While application data is not stored in this class, it is
 * expected that each instance of Form is only used with one application.
 * Multiple application instances would require multiple instances of Form.
 */
class Form {
    constructor(formDefinition, options) {
        var _a, _b, _c;
        this.nestedRuleEngines = {};
        this.mappingEngine = new rules_engine_1.RulesEngine();
        this.accountingCodeEngine = new rules_engine_1.RulesEngine();
        this.errorState = {};
        this.visibilityState = {};
        this.enabledState = {};
        this.applicationMessage = {};
        // Nested object state
        this.repeatableFields = {};
        this.nestedErrorState = {};
        this.nestedVisibilityState = {};
        this.nestedEnabledState = {};
        this.eventEmitter = new events_1.EventEmitter();
        /**
         * Register a listener for the `validationChange` event.  This event is
         * emitted after a call to `evaluateApplicationData` for each application
         * field whose validation state has changed (either from valid to invalid
         * or vice-versa).  All fields are assumed to be valid to start with, so
         * the first call to `evaluateApplicationData` can only generate
         * `validationChange` events if fields become invalid.
         */
        this.onValidationChange = (listener) => {
            this.eventEmitter.on('validationChange', listener);
            return this;
        };
        /**
         * Unregisters a previously registered validation change listener.
         */
        this.removeValidationChangeListener = (listener) => {
            this.eventEmitter.removeListener('validationChange', listener);
            return this;
        };
        /**
         * Register a listener for the `visibilityChange` event.  This event is
         * emitted after a call to `evaluateApplicationData` for each application
         * field whose visibility state hass changed (either from visible to hidden
         * or vice-versa).  All fields are assumed to be visible to start with, so
         * the first call to `evaluateApplicationData` can only generate
         * `visibilityChange` events if fields become hidden.
         */
        this.onVisibilityChange = (listener) => {
            this.eventEmitter.on('visibilityChange', listener);
            return this;
        };
        /**
         * Unregisters a previously registered visibility change listener.
         */
        this.removeVisibilityChangeListener = (listener) => {
            this.eventEmitter.removeListener('visibilityChange', listener);
            return this;
        };
        /**
         * Register a listener for the `enabledChange` event.  This event is
         * emitted after a call to `evaluateApplicationData` for each application
         * field whose enabled state has changed (either from enabled to disabled
         * or vice-versa).  All fields are assumed to be enabled to start with, so
         * the first call to `evaluateApplicationData` can only generate
         * `enabledChange` events if fields become disabled.
         */
        this.onEnabledChange = (listener) => {
            this.eventEmitter.on('enabledChange', listener);
            return this;
        };
        /**
         * Unregisters a previously registered Enabled change listener.
         */
        this.removeEnabledChangeListener = (listener) => {
            this.eventEmitter.removeListener('enabledChange', listener);
            return this;
        };
        /**
         * Registers a listener for the `sectionUpdate` event.  This event is
         * emitted after a call to `evaluateApplicationData` and contains the error
         * and visibility state of every form section of the application.
         */
        this.onSectionUpdate = (listener) => {
            this.eventEmitter.on('sectionUpdate', listener);
            return this;
        };
        /**
         * Unregisters a previously registered section update listener.
         */
        this.removeSectionUpdateListener = (listener) => {
            this.eventEmitter.removeListener('sectionUpdate', listener);
            return this;
        };
        /**
         * Generates initial data for a new application of this form.
         */
        this.newApplicationData = () => {
            const intializeEntries = lodash_1.flatMap(this.allFormEntries).filter((entry) => !lodash_1.isNil(entry.initialValue));
            return Object.assign({}, ...intializeEntries.map((entry) => {
                if (typeof entry.initialValue === 'object' && entry.initialValue._relativeDate) {
                    return {
                        [entry.field]: moment_1.default()
                            .add(entry.initialValue._relativeDate, 'd')
                            .format('YYYY-MM-DD')
                            .toString(),
                    };
                }
                return { [entry.field]: entry.initialValue };
            }));
        };
        /**
         * Evaluate form rules based on the specified application data.  Note that
         * this is a stateful operation; evaluation results are dependent on prior
         * invocations of this function since change events are only emitted when
         * the state (visibility or validation) changes.
         */
        this.evaluateApplicationData = async (applicationData, options) => {
            await this.runRuleEngine(this.ruleEngine, {
                ...applicationData,
                [visibilityStateFact]: this.visibilityState,
                [enabledStateFact]: this.enabledState,
            }, '', this.validatedEntries, this.errorState, options);
            let nestedFields = Object.keys(this.repeatableFields);
            if (options === null || options === void 0 ? void 0 : options.validateDefinedOnly) {
                nestedFields = nestedFields.filter((field) => applicationData[field] !== undefined);
            }
            if (!(options === null || options === void 0 ? void 0 : options.disableNestedRules)) {
                await Promise.all(nestedFields.map(async (field) => {
                    const fieldValue = applicationData[field];
                    // Fields with typeId=multiple should always have arrays as the field data value, but
                    // check anyway for sanity (and to enable typescript type-narrowing to avoid casting).
                    if (lodash_1.isArray(fieldValue)) {
                        const nestedFieldStates = await Promise.all(fieldValue.map(async (nestedData, index) => {
                            let errorState = this.nestedErrorState[field][index];
                            let visibilityState = this.nestedVisibilityState[field][index];
                            let enabledState = this.nestedEnabledState[field][index];
                            if (errorState === undefined) {
                                // If errorState is undefined, visibilityState and enabledState will be as well.
                                errorState = {};
                                visibilityState = {};
                                enabledState = {};
                                // Initialize all fields to valid (no error), visible, and enabled.
                                this.repeatableFields[field].fields.forEach((nestedEntry) => {
                                    errorState[nestedEntry.field] = { error: false, messages: [] };
                                    visibilityState[nestedEntry.field] = true;
                                    enabledState[nestedEntry.field] = true;
                                });
                            }
                            if (this.nestedRuleEngines[field]) {
                                await this.runRuleEngine(this.nestedRuleEngines[field], {
                                    ...nestedData,
                                    [nestedFormInstanceFact]: index,
                                    [visibilityStateFact]: visibilityState,
                                    [enabledStateFact]: enabledState,
                                }, `${field}[${index}]`, this.repeatableFields[field].fields, errorState, options);
                            }
                            return { errorState, visibilityState, enabledState };
                        }));
                        this.nestedErrorState[field] = nestedFieldStates.map((states) => states.errorState);
                        this.nestedVisibilityState[field] = nestedFieldStates.map((states) => states.visibilityState);
                        this.nestedEnabledState[field] = nestedFieldStates.map((states) => states.enabledState);
                    }
                }));
            }
            // Check for duplicate entries.
            Object.values(this.repeatableFields).forEach((entry) => {
                var _a;
                const key = entry.key;
                const fieldData = applicationData[entry.field];
                if (key && lodash_1.isArray(fieldData)) {
                    const keyValues = fieldData.map((item) => item[key]).filter((value) => value);
                    const uniqueValues = new Set(keyValues);
                    if (keyValues.length !== uniqueValues.size && !this.errorState[entry.field].error) {
                        const messages = ['Duplicates not allowed'];
                        this.errorState[entry.field] = { error: true, messages: messages };
                        this.eventEmitter.emit('validationChange', entry.field, true, messages, '');
                    }
                    else if (keyValues.length === uniqueValues.size &&
                        this.errorState[entry.field].error &&
                        !((_a = entry.rules) === null || _a === void 0 ? void 0 : _a.length)) {
                        this.errorState[entry.field] = { error: false, messages: [] };
                        this.eventEmitter.emit('validationChange', entry.field, false, [], '');
                    }
                }
            });
            // Update section status.
            const sectionStatuses = this.definition.sections.map((section) => ({
                errors: section.entries.filter((entry) => this.fieldHasErrors(entry.field)).length,
                visible: lodash_1.some(section.entries, (entry) => this.isFieldVisible(entry.field)),
                enabled: lodash_1.some(section.entries, (entry) => this.isFieldEnabled(entry.field)),
            }));
            this.eventEmitter.emit('sectionUpdate', sectionStatuses);
            // Retrieve field statuses
            const fieldStatuses = this.allFormEntries
                .map((entry) => {
                return {
                    [entry.field]: {
                        visible: this.isFieldVisible(entry.field),
                        enabled: this.isFieldEnabled(entry.field),
                        error: this.fieldHasErrors(entry.field),
                        errorMessages: this.errorState[entry.field].messages,
                    },
                };
            })
                .reduce((aggregate, entry) => Object.assign(aggregate, entry), {});
            return { sections: sectionStatuses, fields: fieldStatuses };
        };
        /**
         * Evaluate rules on a nested form for a multi-occurrence field.  Fields
         * with typeId="multiple" can include multiple instances of data conforming
         * to a nested form definition for that field.  This function evaluates
         * rules for that nested form for a single instance of the field data.
         * Events emitted as a result of this function will have their context
         * set to the name of the multi-occurrence field.
         *
         * As with evaluateApplicationData(), this function is stateful and
         * remembers evaluation results from prior invocations.  For meaningful
         * results, resetMultiFieldData() should be called whenever a new nested
         * form is being evaluated, either for a different multi-occurrence field
         * or a new instance of the same multi-occurrence field.
         */
        this.evaluateMultiFieldData = async (field, fieldData, entryIndex, applicationData, options) => {
            const engine = this.nestedRuleEngines[field];
            const repeatableField = this.repeatableFields[field];
            // Initialize if state is empty.
            if (!this.multiFieldInstanceErrorState) {
                const errorState = {};
                const visibilityState = {};
                const enabledState = {};
                repeatableField === null || repeatableField === void 0 ? void 0 : repeatableField.fields.forEach((entry) => {
                    errorState[entry.field] = { error: false, messages: [] };
                    visibilityState[entry.field] = true;
                    enabledState[entry.field] = true;
                });
                this.multiFieldInstanceErrorState = errorState;
                this.multiFieldInstanceVisibilityState = visibilityState;
                this.multiFieldInstanceEnabledState = enabledState;
            }
            if (engine) {
                await this.runRuleEngine(engine, {
                    ...fieldData,
                    [visibilityStateFact]: this.multiFieldInstanceVisibilityState,
                    [enabledStateFact]: this.multiFieldInstanceEnabledState,
                }, field, repeatableField.fields, this.multiFieldInstanceErrorState, options);
            }
            const currentData = applicationData[field];
            const key = repeatableField === null || repeatableField === void 0 ? void 0 : repeatableField.key;
            // Check for duplicate entries.
            if (key) {
                const keyField = repeatableField.fields.find((nestedField) => nestedField.field === key);
                if (keyField && lodash_1.isArray(currentData)) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    let otherEntries;
                    if (entryIndex !== null) {
                        // Remove existing entry for the purposes of the duplicate check
                        // since it will be replaced by the new values.
                        otherEntries = [...currentData.slice(0, entryIndex), ...currentData.slice(entryIndex + 1)];
                    }
                    else {
                        otherEntries = currentData;
                    }
                    // Current set of key values already in the application data.  Null
                    // or undefined don't count as values (if by chance some entries don't
                    // have a value for the key field).
                    const currentKeys = new Set(otherEntries.map((item) => item[keyField.field]).filter((value) => value));
                    if (currentKeys.has(fieldData[keyField.field]) &&
                        !this.multiFieldInstanceErrorState[keyField.field].error) {
                        const messages = [repeatableField.duplicateKeyMessage || `You already have this ${keyField.label}`];
                        this.multiFieldInstanceErrorState[keyField.field] = {
                            error: true,
                            messages: messages,
                        };
                        this.eventEmitter.emit('validationChange', keyField.field, true, messages, field);
                    }
                }
            }
        };
        /**
          Generates initial data for a new instance of a multiField
         */
        this.initializeMultiFieldInstance = (multiField) => {
            var _a;
            const multiFieldEntry = this.allFormEntries.find((entry) => entry.field === multiField);
            const initialValues = {};
            (_a = multiFieldEntry === null || multiFieldEntry === void 0 ? void 0 : multiFieldEntry.fields) === null || _a === void 0 ? void 0 : _a.forEach((field) => {
                if (!lodash_1.isNil(field.initialValue)) {
                    if (typeof field.initialValue === 'object' && field.initialValue._relativeDate) {
                        initialValues[field.field] = moment_1.default()
                            .add(field.initialValue._relativeDate, 'd')
                            .format('YYYY-MM-DD')
                            .toString();
                    }
                    else {
                        initialValues[field.field] = field.initialValue;
                    }
                }
            });
            return initialValues;
        };
        this.resetMultiFieldData = () => {
            this.multiFieldInstanceErrorState = undefined;
            this.multiFieldInstanceVisibilityState = undefined;
            this.multiFieldInstanceEnabledState = undefined;
        };
        /**
         * Returns whether the application has any errors based on prior invocations
         * of `evaluateApplicationData`.
         */
        this.applicationHasErrors = () => {
            return lodash_1.some(this.allFormEntries, (entry) => this.fieldHasErrors(entry.field));
        };
        /**
         * Returns whether the specified form field in the application has any
         * errors based on prior invocations of `evaluateApplicationData`.
         */
        this.fieldHasErrors = (field) => {
            return (this.isFieldVisible(field) &&
                this.isFieldEnabled(field) &&
                (this.errorState[field].error || this.nestedFieldDataHasErrors(field)));
        };
        this.nestedFieldDataHasErrors = (field) => {
            // errorStates and visibilityStates should always have the same length.
            const errorStates = this.nestedErrorState[field] || [];
            const visibilityStates = this.nestedVisibilityState[field] || [];
            const enabledStates = this.nestedEnabledState[field] || [];
            return lodash_1.some(errorStates, (errorState, index) => lodash_1.some(Object.keys(errorState), (key) => errorState[key].error && visibilityStates[index][key] && enabledStates[index][key]));
        };
        /**
         * Returns whether the specified form field in the application should be
         * visible to the applicant based on prior invocations of
         * `evaluateApplicationData`.
         */
        this.isFieldVisible = (field) => {
            return this.visibilityState[field];
        };
        /**
         * Returns whether the specified form field in the application should be
         * enabled based on prior invocations of
         * `evaluateApplicationData`.
         */
        this.isFieldEnabled = (field) => {
            return this.enabledState[field];
        };
        /**
         * Returns the accounting code based on the application type with a set of rules.
         */
        this.generateAccountingCode = async (data) => {
            var _a, _b, _c, _d;
            const rules = (_a = this.definition.accountingCode) !== null && _a !== void 0 ? _a : [];
            const ruleProperties = rules.map((rule, index) => {
                let conditions = rule.conditions;
                // value rules with no conditions should always evaluate to true
                if (!rule.conditions) {
                    conditions = {
                        any: [
                            {
                                fact: internalFact('truthy'),
                                operator: 'equal',
                                value: true,
                            },
                        ],
                    };
                }
                return {
                    conditions: conditions,
                    event: {
                        type: 'accountingCode',
                        params: {
                            value: rule.value,
                        },
                    },
                    priority: rules.length - index,
                    onSuccess: this.setAccountingCode,
                };
            });
            this.accountingCodeEngine = new rules_engine_1.RulesEngine([...ruleProperties], {
                allowUndefinedFacts: true,
                tenant: { timezone: (_c = (_b = this.options) === null || _b === void 0 ? void 0 : _b.tenant) === null || _c === void 0 ? void 0 : _c.timezone },
                axios: (_d = this.options) === null || _d === void 0 ? void 0 : _d.axios,
            });
            await this.accountingCodeEngine.run({ ...data, [internalFact('truthy')]: true });
            return this.accountingCode;
        };
        /**
         * Returns the data object and the attachments list for the message format
         * based on the defined mappings in the form definition.
         */
        this.generateMessageData = async (data) => {
            this.applicationMessage = {};
            await this.mapDerivedValues(data);
            let messageAttachments = [];
            // Map non-derived values to message format
            this.allFormEntries.forEach((entry) => {
                var _a, _b;
                let value = data[entry.field];
                const jsonPath = (_a = entry.fieldMapping) === null || _a === void 0 ? void 0 : _a.targetPath;
                const operator = (_b = entry.fieldMapping) === null || _b === void 0 ? void 0 : _b.operator;
                if (jsonPath && this.isFieldVisible(entry.field) && this.isFieldEnabled(entry.field)) {
                    if (entry.typeId === 'multiple' && value) {
                        const recordData = value;
                        const valueMultiple = recordData
                            .map((record, index) => {
                            var _a;
                            const recordMapping = {};
                            (_a = entry.fields) === null || _a === void 0 ? void 0 : _a.forEach((subEntry) => {
                                var _a;
                                let subValue = record[subEntry.field];
                                const subPath = (_a = subEntry.fieldMapping) === null || _a === void 0 ? void 0 : _a.targetPath;
                                if (subPath &&
                                    this.nestedVisibilityState[entry.field][index][subEntry.field] &&
                                    this.nestedEnabledState[entry.field][index][subEntry.field]) {
                                    if (subEntry.typeId === 'boolean' && !subValue) {
                                        subValue = false;
                                    }
                                    else if (subValue === '') {
                                        subValue = undefined;
                                    }
                                    jsonpath_1.default.value(recordMapping, subPath, subValue);
                                }
                            });
                            return lodash_1.isEmpty(recordMapping) ? undefined : recordMapping;
                        })
                            .filter((record) => record);
                        value = valueMultiple.length > 0 ? valueMultiple : undefined;
                    }
                    if (entry.typeId === 'boolean' && !value) {
                        value = false;
                    }
                    else if (value === '') {
                        value = undefined;
                    }
                    if (operator === 'append') {
                        value = this.appendMappedValue(jsonpath_1.default.value(this.applicationMessage, jsonPath), value);
                    }
                    jsonpath_1.default.value(this.applicationMessage, jsonPath, value);
                }
                // Add uploaded files to message attachments
                if (entry.typeId === 'fileUpload' || entry.typeId === 'photoUpload') {
                    const files = data[entry.field];
                    if (files) {
                        if (Array.isArray(files)) {
                            const fileIds = files.map((file) => file.id);
                            messageAttachments = [...messageAttachments, ...fileIds];
                        }
                        else {
                            messageAttachments = [...messageAttachments, files.id];
                        }
                    }
                }
            });
            return { data: this.applicationMessage, attachments: messageAttachments };
        };
        this.multiFieldDataHasErrors = () => {
            const errorState = this.multiFieldInstanceErrorState || {};
            const visibilityState = this.multiFieldInstanceVisibilityState || {};
            const enabledState = this.multiFieldInstanceEnabledState || {};
            return lodash_1.some(Object.keys(errorState), (key) => errorState[key].error && visibilityState[key] && enabledState[key]);
        };
        this.findAllRuleConditions = () => {
            const topLevelFieldConditions = this.findFieldConditions(this.allFormEntries);
            const repeatableFields = this.allFormEntries.filter((entry) => { var _a; return (_a = entry.fields) === null || _a === void 0 ? void 0 : _a.length; });
            const allNestedFields = lodash_1.flatMap(repeatableFields, (entry) => { var _a; return (_a = entry.fields) !== null && _a !== void 0 ? _a : []; });
            const nestedFieldConditions = this.findFieldConditions(allNestedFields);
            return [...topLevelFieldConditions, ...nestedFieldConditions];
        };
        this.findFieldConditions = (fields) => {
            const validationConditions = lodash_1.flatMap(fields, (entry) => { var _a; return (_a = entry.rules) !== null && _a !== void 0 ? _a : []; }).map((rule) => rule.conditions);
            const visibilityConditions = fields
                .filter((entry) => entry.visibilityConditions)
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                .map((entry) => entry.visibilityConditions);
            const enabledConditions = fields
                .filter((entry) => entry.enabledConditions)
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                .map((entry) => entry.enabledConditions);
            return [...validationConditions, ...visibilityConditions, ...enabledConditions];
        };
        this.normalizeNestedCondition = (condition) => {
            if ('all' in condition || 'any' in condition) {
                this.normalizeTopLevelCondition(condition);
            }
            else if (regexOperators.includes(condition.operator)) {
                if (lodash_1.isString(condition.value)) {
                    condition.value = stringToRegex(condition.value);
                }
                else if (lodash_1.isString(condition.value.fieldValue)) {
                    condition.value.fieldValue = stringToRegex(condition.value.fieldValue);
                }
            }
        };
        this.normalizeTopLevelCondition = (condition) => {
            if ('all' in condition) {
                condition.all.forEach(this.normalizeNestedCondition);
            }
            else {
                condition.any.forEach(this.normalizeNestedCondition);
            }
        };
        this.normalizeFormDefinition = () => {
            // Check text field masks for regex expressions and convert them.
            const maskedEntries = [];
            this.allFormEntries.forEach((entry) => {
                if (entry.fields) {
                    entry.fields.forEach((subEntry) => {
                        if (subEntry.mask) {
                            maskedEntries.push(subEntry);
                        }
                    });
                }
                else if (entry.mask) {
                    maskedEntries.push(entry);
                }
            });
            maskedEntries.forEach((entry) => {
                var _a;
                // Each element corresponds with one character, so for static content, the element string should
                // only be one character.  Anything bigger than one character is assumed to be a regex.
                if (entry.mask === 'email') {
                    entry.mask = text_mask_addons_1.emailMask.mask;
                }
                else if (lodash_1.isArray(entry.mask)) {
                    entry.mask = (_a = entry.mask) === null || _a === void 0 ? void 0 : _a.map((element) => lodash_1.isString(element) && element.length > 1 ? stringToRegex(element) : element);
                }
            });
            // Check rule conditions for regex operations.
            this.findAllRuleConditions().forEach(this.normalizeTopLevelCondition);
        };
        this.runRuleEngine = async (engine, data, dataContext, entriesToValidate, errorState, options) => {
            const result = await engine.run(this.sanitizeData(data));
            if (options === null || options === void 0 ? void 0 : options.validateDefinedOnly) {
                // Only validate entries which have data or are currently in an error
                // state.  A full form validation can flag a field with no value as
                // having an error, but a subsequent partial validation should be
                // allowed to unflag that field (e.g. if other fields changed such
                // that the field with no value is no longer required).
                entriesToValidate = entriesToValidate.filter((entry) => data[entry.field] !== undefined || errorState[entry.field].error);
            }
            // Check fields for validation errors.
            entriesToValidate.forEach((entry) => {
                this.checkEntryResults(entry, dataContext, errorState, result);
            });
        };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.sanitizeData = (data) => {
            // Remove properties explicitly set to undefined as json-rules-engine
            // will throw errors if it encounters them.  A property set to undefined
            // is essentially the same as not having the property at all.
            return Object.keys(data)
                .filter((key) => data[key] !== undefined)
                .map((key) => ({ [key]: data[key] }))
                .reduce((aggregate, entry) => ({ ...aggregate, ...entry }));
        };
        this.checkEntryResults = (entry, entryContext, errorState, engineResult) => {
            var _a;
            // Rule engine only returns successful events.  To check if any rule
            // failed, look at all the rules defined for the form field and see if
            // any of them are missing from the rule engine results.
            const failedEvents = lodash_1.differenceBy((_a = entry.rules) === null || _a === void 0 ? void 0 : _a.map((rule) => rule.event), engineResult.events, 'type');
            const newErrorState = failedEvents.length > 0;
            const newMessages = failedEvents.map((evt) => { var _a; return (_a = evt.params) === null || _a === void 0 ? void 0 : _a.message; });
            if (errorState[entry.field].error !== newErrorState ||
                !lodash_1.isEqual(errorState[entry.field].messages, newMessages)) {
                errorState[entry.field] = {
                    error: newErrorState,
                    messages: newMessages,
                };
                this.eventEmitter.emit('validationChange', entry.field, newErrorState, newMessages, entryContext);
            }
        };
        this.mapDerivedValues = async (data) => {
            var _a, _b, _c, _d, _e;
            // Need to use for loop instead of forEach so that
            // rule engines are run asynchronously
            for (const targetMapping of (_b = (_a = this.definition.formMappings) === null || _a === void 0 ? void 0 : _a.derivedMappings) !== null && _b !== void 0 ? _b : []) {
                const targetRules = targetMapping.valueRules.map((rule, index) => {
                    let conditions;
                    // value rules with no conditions should always evaluate to true
                    if (rule.conditions) {
                        conditions = rule.conditions;
                    }
                    else {
                        conditions = {
                            any: [
                                {
                                    fact: internalFact('truthy'),
                                    operator: 'equal',
                                    value: true,
                                },
                            ],
                        };
                    }
                    return {
                        conditions: conditions,
                        event: {
                            type: 'derivedMapping',
                            params: {
                                value: rule.value,
                                target: targetMapping.targetPath,
                                operator: targetMapping.operator,
                            },
                        },
                        priority: targetMapping.valueRules.length - index,
                        onSuccess: this.setTargetValue,
                    };
                });
                this.mappingEngine = new rules_engine_1.RulesEngine(targetRules, {
                    allowUndefinedFacts: true,
                    tenant: { timezone: (_d = (_c = this.options) === null || _c === void 0 ? void 0 : _c.tenant) === null || _d === void 0 ? void 0 : _d.timezone },
                    axios: (_e = this.options) === null || _e === void 0 ? void 0 : _e.axios,
                });
                await this.mappingEngine.run({ ...data, [internalFact('truthy')]: true });
            }
        };
        this.handleShowField = (event, almanac) => {
            this.handleFieldVisibility(event, true, almanac);
        };
        this.handleHideField = (event, almanac) => {
            this.handleFieldVisibility(event, false, almanac);
        };
        this.handleFieldVisibility = async (event, newVisibilityState, almanac) => {
            var _a;
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const field = event.params.field;
            const visibilityState = await almanac.factValue(visibilityStateFact);
            if (visibilityState[field] !== newVisibilityState) {
                visibilityState[field] = newVisibilityState;
                let fieldContext = ((_a = event.params) === null || _a === void 0 ? void 0 : _a.fieldContext) || '';
                const instance = await almanac.factValue(nestedFormInstanceFact);
                if (instance !== undefined) {
                    fieldContext = `${fieldContext}[${instance}]`;
                }
                this.eventEmitter.emit('visibilityChange', field, newVisibilityState, fieldContext);
            }
        };
        this.setAccountingCode = (event) => {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            this.accountingCode = event.params.value;
            this.accountingCodeEngine.stop();
        };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.convertTargetValue = async (value, almanac) => {
            if (!!value && typeof value === 'object' && value.fact) {
                return await almanac.factValue(value.fact, value.params, value.path);
            }
            return value;
        };
        this.setTargetValue = async (event, almanac) => {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const { target, value, operator } = event.params;
            if (operator === 'append') {
                const previousValue = jsonpath_1.default.value(this.applicationMessage, target);
                const appendedValue = this.appendMappedValue(previousValue, await this.convertTargetValue(value, almanac));
                jsonpath_1.default.value(this.applicationMessage, target, appendedValue);
            }
            else {
                jsonpath_1.default.value(this.applicationMessage, target, await this.convertTargetValue(value, almanac));
                this.mappingEngine.stop();
            }
        };
        this.appendMappedValue = (originalValue, valueToAppend) => {
            const isArray = Array.isArray;
            if (!valueToAppend) {
                if (isArray(originalValue)) {
                    return originalValue;
                }
                else {
                    return originalValue ? [originalValue] : null;
                }
            }
            else {
                if (isArray(originalValue) && isArray(valueToAppend)) {
                    return [...originalValue, ...valueToAppend];
                }
                else if (isArray(originalValue) && !isArray(valueToAppend)) {
                    return [...originalValue, valueToAppend];
                }
                else if (!isArray(originalValue) && isArray(valueToAppend)) {
                    return valueToAppend;
                }
                else {
                    return [valueToAppend];
                }
            }
        };
        this.handleDisableField = (event, almanac) => {
            this.handleFieldEnabledStatus(event, false, almanac);
        };
        this.handleEnableField = (event, almanac) => {
            this.handleFieldEnabledStatus(event, true, almanac);
        };
        this.handleFieldEnabledStatus = async (event, newEnabledState, almanac) => {
            var _a;
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const field = event.params.field;
            const enabledState = await almanac.factValue(enabledStateFact);
            if (enabledState[field] !== newEnabledState) {
                enabledState[field] = newEnabledState;
                let fieldContext = ((_a = event.params) === null || _a === void 0 ? void 0 : _a.fieldContext) || '';
                const instance = await almanac.factValue(nestedFormInstanceFact);
                if (instance !== undefined) {
                    fieldContext = `${fieldContext}[${instance}]`;
                }
                this.eventEmitter.emit('enabledChange', field, newEnabledState, fieldContext);
            }
        };
        // This needs to come first, as everything else relies on these being populated.
        this.definition = formDefinition;
        this.options = options;
        this.definition.sections = this.definition.sections.map((section) => {
            if (section.visibilityConditions) {
                return {
                    ...section,
                    entries: section.entries.map((entry) => {
                        if (entry.visibilityConditions) {
                            return {
                                ...entry,
                                visibilityConditions: {
                                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                    all: [section.visibilityConditions, entry.visibilityConditions],
                                },
                            };
                        }
                        return { ...entry, visibilityConditions: section.visibilityConditions };
                    }),
                };
            }
            return section;
        });
        this.allFormEntries = lodash_1.flatMap(this.definition.sections, (section) => section.entries);
        this.normalizeFormDefinition();
        // Initialize state, assuming all fields are visible, have valid data, and are enabled.
        this.allFormEntries.forEach((entry) => {
            this.errorState[entry.field] = { error: false, messages: [] };
            this.visibilityState[entry.field] = true;
            this.enabledState[entry.field] = true;
        });
        this.validatedEntries = this.allFormEntries.filter((entry) => entry.rules && entry.rules.length > 0);
        // Compile list of top-level validation rules.
        const validationRules = lodash_1.flatMap(this.validatedEntries, 
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        (entry) => entry.rules);
        // Compile list of visibility rules.
        const visibilityRules = lodash_1.map(this.allFormEntries.filter((entry) => entry.visibilityConditions), (entry) => ({
            conditions: entry.visibilityConditions,
            event: {
                type: 'visibility',
                params: {
                    field: entry.field,
                    fieldContext: '',
                },
            },
            onSuccess: this.handleShowField,
            onFailure: this.handleHideField,
        }));
        // Compile list of enabled rules.
        const enabledRules = lodash_1.map(this.allFormEntries.filter((entry) => entry.enabledConditions), (entry) => ({
            conditions: entry.enabledConditions,
            event: {
                type: 'enabledChange',
                params: {
                    field: entry.field,
                    fieldContext: '',
                },
            },
            onSuccess: this.handleEnableField,
            onFailure: this.handleDisableField,
        }));
        // Check for rules within nested fields.
        this.allFormEntries
            .filter((entry) => entry.fields && entry.fields.length > 0)
            .forEach((entry) => {
            var _a, _b, _c, _d, _e;
            this.repeatableFields[entry.field] = entry;
            this.nestedErrorState[entry.field] = [];
            this.nestedVisibilityState[entry.field] = [];
            this.nestedEnabledState[entry.field] = [];
            let nestedValidationRules = [];
            let nestedVisibilityRules = [];
            let nestedEnabledRules = [];
            if (lodash_1.some(entry.fields, (nestedEntry) => nestedEntry.rules && nestedEntry.rules.length > 0)) {
                nestedValidationRules = lodash_1.flatMap(this.repeatableFields[entry.field].fields, (entry) => entry.rules || []);
            }
            const dynamicFields = ((_a = entry.fields) === null || _a === void 0 ? void 0 : _a.filter((nestedEntry) => nestedEntry.visibilityConditions)) || [];
            const dynamicallyEnabledFields = ((_b = entry.fields) === null || _b === void 0 ? void 0 : _b.filter((nestedEntry) => nestedEntry.enabledConditions)) || [];
            if (dynamicFields.length > 0) {
                nestedVisibilityRules = dynamicFields.map((nestedEntry) => ({
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    conditions: nestedEntry.visibilityConditions,
                    event: {
                        type: 'visibility',
                        params: {
                            field: nestedEntry.field,
                            fieldContext: entry.field,
                        },
                    },
                    onSuccess: this.handleShowField,
                    onFailure: this.handleHideField,
                }));
            }
            if (dynamicallyEnabledFields.length > 0) {
                nestedEnabledRules = dynamicallyEnabledFields.map((nestedEntry) => ({
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    conditions: nestedEntry.enabledConditions,
                    event: {
                        type: 'enabledChange',
                        params: {
                            field: nestedEntry.field,
                            fieldContext: entry.field,
                        },
                    },
                    onSuccess: this.handleEnableField,
                    onFailure: this.handleDisableField,
                }));
            }
            if (nestedValidationRules.length > 0 ||
                nestedVisibilityRules.length > 0 ||
                nestedEnabledRules.length > 0) {
                this.nestedRuleEngines[entry.field] = new rules_engine_1.RulesEngine([...nestedValidationRules, ...nestedVisibilityRules, ...nestedEnabledRules], {
                    allowUndefinedFacts: true,
                    tenant: { timezone: (_d = (_c = this.options) === null || _c === void 0 ? void 0 : _c.tenant) === null || _d === void 0 ? void 0 : _d.timezone },
                    axios: (_e = this.options) === null || _e === void 0 ? void 0 : _e.axios,
                });
            }
        });
        this.ruleEngine = new rules_engine_1.RulesEngine([...validationRules, ...visibilityRules, ...enabledRules], {
            allowUndefinedFacts: true,
            tenant: { timezone: (_b = (_a = this.options) === null || _a === void 0 ? void 0 : _a.tenant) === null || _b === void 0 ? void 0 : _b.timezone },
            axios: (_c = this.options) === null || _c === void 0 ? void 0 : _c.axios,
        });
    }
}
exports.Form = Form;
exports.default = Form;
//# sourceMappingURL=form.js.map